// Single source of truth for the PWHL Seasons editor stat columns.
// Mirrors the public Player Profile season-stats columns (statColumns.js).
//
// `derived: true` columns are computed ratios (read-only everywhere — never
// manual). Every non-derived column is "manual-eligible": admins can mark it
// Manual (typed) or leave it Auto (summed from Game Centre game logs) via the
// global PWHL Stat Mode config in Admin → Settings.

export const PWHL_SKATER_STAT_COLS = [
    { key: "gp", label: "GP" },
    { key: "g", label: "G" },
    { key: "a", label: "A" },
    { key: "p", label: "PTS", derived: true },
    { key: "plus_minus", label: "+/-" },
    { key: "sh", label: "SH" },
    { key: "pim", label: "PIM" },
    { key: "fow", label: "FO" },
    { key: "wf_pct", label: "WF%", derived: true, step: "0.1" },
    { key: "ppg", label: "PPG" },
    { key: "shg", label: "SHG" },
    { key: "gwg", label: "GWG" },
    { key: "sog", label: "SOG" },
    { key: "soa", label: "SOA" },
    { key: "toi", label: "TOI", text: true },
    { key: "blk", label: "BLK" },
    { key: "hits", label: "Hits" },
];

export const PWHL_GOALIE_STAT_COLS = [
    { key: "gp", label: "GP" },
    { key: "ga", label: "GA" },
    { key: "minutes", label: "Min" },
    { key: "svs", label: "SVS" },
    { key: "gaa", label: "GAA", derived: true, step: "0.01" },
    { key: "sv_pct", label: "SV%", derived: true, step: "0.001" },
    { key: "w", label: "W" },
    { key: "l", label: "L" },
    { key: "ot", label: "OT" },
    { key: "sol", label: "SOL" },
    { key: "so", label: "SO" },
    { key: "g_goalie", label: "G" },
    { key: "a_goalie", label: "A" },
    { key: "pim_goalie", label: "PIM" },
];

export const pwhlStatCols = (isGoalie) => (isGoalie ? PWHL_GOALIE_STAT_COLS : PWHL_SKATER_STAT_COLS);

// Non-derived (manual-eligible) keys, used by the Settings auto/manual toggle.
export const PWHL_MANUAL_ELIGIBLE = {
    skater: PWHL_SKATER_STAT_COLS.filter((c) => !c.derived).map((c) => c.key),
    goalie: PWHL_GOALIE_STAT_COLS.filter((c) => !c.derived).map((c) => c.key),
};

// Client-side derived-ratio computation (matches backend _recompute_pwhl_derived).
export function computePwhlDerived(row, isGoalie) {
    const n = (k) => Number(row[k]) || 0;
    if (isGoalie) {
        const mins = n("minutes"), ga = n("ga"), svs = n("svs");
        const sa = svs + ga;
        return {
            gaa: mins ? (ga * 60) / mins : 0,
            sv_pct: sa ? svs / sa : 0,
        };
    }
    const g = n("g"), a = n("a"), sog = n("sog"), fow = n("fow"), fo = n("fo"), soa = n("soa"), sowg = n("sowg");
    return {
        p: g + a,
        sh_pct: sog ? (g / sog) * 100 : 0,
        wf_pct: fo ? (fow / fo) * 100 : 0,
        so_pct: soa ? (sowg / soa) * 100 : 0,
    };
}

// Display formatting for derived columns in the editor.
export function fmtDerived(key, value) {
    const v = Number(value) || 0;
    if (key === "sv_pct") return v >= 1 ? "1.000" : v.toFixed(3).replace(/^0\./, ".");
    if (key === "gaa") return v.toFixed(2);
    if (key === "wf_pct" || key === "sh_pct" || key === "so_pct") return v.toFixed(1);
    return String(v);
}
